import { AlertTriangle, Banknote, CheckCircle2, CircleDashed, Coins, EyeOff, Info, ShieldQuestion } from 'lucide-react';
import type { PairListSummary } from '../../types/customerDuplicates';
import { ALL_REASONS, REASON_LABELS, COMPANY_LABELS, parseRunScope } from '../../types/customerDuplicates';

interface Props {
    summary: PairListSummary;
    activeReason: string;
    activeCompany: string;
    activeLevel: string;
    activePresence: string;
    onReasonClick: (reason: string) => void;
    onCompanyClick: (company: string) => void;
    onLevelClick: (level: string) => void;
    onPresenceClick: (presence: string) => void;
}

function eur(n: number): string {
    return n.toLocaleString('de-CH', { maximumFractionDigits: 0 });
}

/**
 * The Kennzahlenzeile (F10 / masterplan §4 phase 3): this is Roman's argument for the Bruno
 * Crippa conversation, on screen, not only in the export. Every tile is a toggle onto the SAME
 * filter set the table below uses, so clicking "VAT" both narrows the list AND re-computes every
 * other number here — including the AR/revenue figures — against exactly that subset.
 *
 * Since 2026-09-29 only living accounts are compared (`customer-duplicate-eligibility.ts`), so
 * the former dormant split of UNCLEAR is gone, and "Blocked, replaced" / "Migration" — both need
 * a dormant side — are empty in the work queue. A reason tile is shown only when it has pairs
 * (or is the active filter), so the retired reasons still surface under "No longer found".
 */
export function KpiRow({
    summary, activeReason, activeCompany, activeLevel, activePresence,
    onReasonClick, onCompanyClick, onLevelClick, onPresenceClick,
}: Props) {
    const decided = Object.entries(summary.byStatus).filter(([s]) => s !== 'OPEN').reduce((a, [, n]) => a + n, 0);
    const open = summary.byStatus.OPEN ?? 0;
    const registryPct = summary.registryCoverage.total > 0
        ? (100 * summary.registryCoverage.checked / summary.registryCoverage.total) : 0;
    const scope = parseRunScope(summary.latestRun);
    const n = (v: number) => v.toLocaleString('de-CH');

    return (
        <div className="space-y-3">
            {/* Which accounts are compared at all — Roman asked for the definition on the page. */}
            <div className="flex items-start gap-1.5 rounded border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs text-blue-900">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                    Only <strong>active accounts</strong> are compared: positive net revenue in the last 24 months
                    (invoices minus credit memos, commission business included). Left out are accounts without it
                    (this also covers every account from the retired source systems AX and CMP), accounts with EOS
                    status <strong>Closed</strong>, and redPoint accounts whose name starts with <strong>ZZZ</strong>.
                    An EOS <em>Blocked</em> account that still trades stays in. A customer who trades again is picked
                    up by the next nightly run.
                    {scope !== null && (
                        <span className="text-blue-700">
                            {' '}Last run: {n(scope.customersCompared)} of {n(scope.customersLoaded)} accounts compared
                            — {n(scope.noRevenue)} without revenue, {n(scope.closed)} closed, {n(scope.zzzName)} ZZZ.
                        </span>
                    )}
                </span>
            </div>

            {/* Reason tiles — the primary breakdown of the whole page. */}
            <div className="flex flex-wrap gap-2">
                {ALL_REASONS.map(reason => {
                    const bucket = summary.byReason[reason];
                    const active = activeReason === reason;
                    if (bucket.total === 0 && !active) return null;
                    return (
                        <button
                            key={reason}
                            onClick={() => onReasonClick(active ? '' : reason)}
                            className={`rounded border px-3 py-2 text-left text-sm transition-colors ${
                                active ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                            }`}
                        >
                            <div className="text-xs text-gray-500">{REASON_LABELS[reason]}</div>
                            <div className="font-semibold text-gray-900">{bucket.total.toLocaleString('de-CH')}</div>
                        </button>
                    );
                })}
            </div>

            <div className="flex flex-wrap gap-2">
                {Object.entries(summary.byCompany).map(([company, n]) => (
                    <button
                        key={company}
                        onClick={() => onCompanyClick(activeCompany === company ? '' : company)}
                        className={`rounded border px-2.5 py-1 text-xs transition-colors ${
                            activeCompany === company ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                    >
                        {COMPANY_LABELS[company] ?? company.toUpperCase()} <span className="font-semibold">{n.toLocaleString('de-CH')}</span>
                    </button>
                ))}
                {(['1', '2'] as const).map(level => (
                    <button
                        key={level}
                        onClick={() => onLevelClick(activeLevel === level ? '' : level)}
                        className={`rounded border px-2.5 py-1 text-xs transition-colors ${
                            activeLevel === level ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                        title={level === '1' ? 'Same legal entity — should be one account' : 'Same firm/group, several sites or companies'}
                    >
                        Level {level} <span className="font-semibold">{(summary.byLevel[level] ?? 0).toLocaleString('de-CH')}</span>
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                <div className="rounded border border-gray-200 bg-white p-3">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500"><Coins className="h-3.5 w-3.5" /> Open AR items</div>
                    <div className="font-semibold text-gray-900">{summary.openItems.toLocaleString('de-CH')}</div>
                    <div className="text-xs text-gray-400">{eur(summary.openAmountEur)} EUR · {summary.accountsWithOpenItems.toLocaleString('de-CH')} accounts</div>
                </div>
                <div className="rounded border border-gray-200 bg-white p-3">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500"><Banknote className="h-3.5 w-3.5" /> Revenue ({summary.revenueWindowMonths}m)</div>
                    <div className="font-semibold text-gray-900">{eur(summary.revenueEur)} EUR</div>
                    <div className="text-xs text-gray-400">{summary.accountsWithRevenue.toLocaleString('de-CH')} accounts</div>
                </div>
                <div className="rounded border border-gray-200 bg-white p-3">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500"><CircleDashed className="h-3.5 w-3.5" /> Open</div>
                    <div className="font-semibold text-gray-900">{open.toLocaleString('de-CH')}</div>
                </div>
                <div className="rounded border border-gray-200 bg-white p-3">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500"><CheckCircle2 className="h-3.5 w-3.5" /> Decided</div>
                    <div className="font-semibold text-gray-900">{decided.toLocaleString('de-CH')}</div>
                </div>
                {/* A pair that stops matching is flagged, never deleted (plan §4 phase 5). Without a
                    way to REACH those rows a case leaves the queue because somebody edited a field
                    in the ERP and nobody ever learns it went — so the count is a filter toggle. */}
                <button
                    onClick={() => onPresenceClick(activePresence === 'GONE' ? 'CURRENT' : 'GONE')}
                    title={
                        `${summary.goneNotDecided.toLocaleString('de-CH')} undecided pair(s) the latest run no longer finds. `
                        + `They are kept, not deleted - usually the trace of an ERP field edit. Click to show only these.`
                    }
                    className={`rounded border p-3 text-left transition-colors ${
                        activePresence === 'GONE'
                            ? 'border-amber-500 bg-amber-50'
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                >
                    <div className="flex items-center gap-1.5 text-xs text-gray-500"><EyeOff className="h-3.5 w-3.5" /> No longer found</div>
                    <div className="font-semibold text-gray-900">{summary.goneNotDecided.toLocaleString('de-CH')}</div>
                </button>
                <div className="rounded border border-gray-200 bg-white p-3" title={
                    `${summary.registryCoverage.checked.toLocaleString('de-CH')} of ${summary.registryCoverage.total.toLocaleString('de-CH')} customers have at least one register check attempted. `
                    + `Both UID webservice and VIES throttle a caller who checks the whole stock at once, so this fills in over roughly two weeks.`
                }>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500"><ShieldQuestion className="h-3.5 w-3.5" /> Register checked</div>
                    <div className="font-semibold text-gray-900">{registryPct.toFixed(1)}%</div>
                    <div className="text-xs text-gray-400">{summary.registryCoverage.checked.toLocaleString('de-CH')} / {summary.registryCoverage.total.toLocaleString('de-CH')}</div>
                </div>
            </div>

            {summary.latestRun?.status === 'PARTIAL' && (
                <div className="flex items-start gap-1.5 rounded border border-red-300 bg-red-50 px-3 py-1.5 text-xs text-red-800">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                        The last detection run (#{summary.latestRun.runId}) finished <strong>INCOMPLETE</strong>, so this
                        queue is missing cases: {summary.latestRun.error ?? 'no reason recorded'}
                    </span>
                </div>
            )}

            {summary.registryCoverage.checked < summary.registryCoverage.total && (
                <div className="flex items-start gap-1.5 rounded border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                        The external VAT/UID register check is still working through the customer stock (throttled by the
                        registers themselves) — a pair whose VAT reason looks unconfirmed may simply not be checked yet,
                        not "no problem found".
                    </span>
                </div>
            )}
        </div>
    );
}
